import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { createColumns } from "components/task-list/lib/constants";
import ReactTablePagination from "components/task-list/components/pagination";
import Modal from "components/modal";
import eventEmitter from "shared/events";
import NoteLoader from "components/notes/components/note-loader";
import { TaskListExternalFilters } from "./components/task-list-external-filters";
import { SECTION_MAP } from "components/task-list/lib/constants";
import { usePagination } from "shared/hooks/use-pagination";
import { useWindowVisible } from "components/table/lib/hooks";
import { TASK_STATUSES } from "./lib/constants";
import { useSearchTasks } from "shared/hooks/api/use-search-task";
import { getStorageValue, setStorageValue } from "shared/storage";
import { DEFAULT_SORT } from "./lib/constants";
import { useLocations } from "components/search-advanced-filters/hooks/use-locations";
import { useNavigate } from "react-router-dom";
import ColumnChooser from "../task-list/components/column-chooser";
import { getUserColumns } from "./lib/utils";
import { TaskStatus } from "../../shared/constants/task-status";
import { useSettings } from "../../shared/hooks/use-settings";

const taskType = "duplicatestaging";
const upcMapper = "context.upc";
const defaultFilterValues = [{ id: "state", value: TaskStatus.TO_DO }];

const DuplicateStagingIndex = () => {
    const { settings } = useSettings();
    const [showModalColumns, setShowModalColumns] = useState(false);
    const section = SECTION_MAP[taskType];
    const navigate = useNavigate();
    const storedFilterName = "dup-stage-filter";
    const storedSettings = getStorageValue("dup-stage-settings") || {};
    const storedSort = getStorageValue("dup-stage-sort");
    const storedFilters = getStorageValue(storedFilterName) || [];
    const storedUpcs = storedFilters?.find(({ id }) => id === "upc");
    const [upcs, setUpcs] = useState(storedUpcs?.value);
    const [taskListItems, setTaskListItems] = useState([]);
    const [noteSettings, setNoteSettings] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState(() => getUserColumns());
    const { data: locations } = useLocations();
    const isWindowVisible = useWindowVisible();
    const { mutate: searchTasks, data: taskList, isSuccess } = useSearchTasks(taskType);
    const [
        { pageNumber, pageSize, sortBy, totalCount, totalPages, searchParams, pagingParams, filteredValues },
        { setPageSize, setPage, setSortBy, setTotalCount, search, searchByUpcs, resetFilters },
    ] = usePagination({
        defaultFilters: storedFilters.length > 0 ? storedFilters : defaultFilterValues,
        defaultValues: {
            pageSize: storedSettings.pageSize || 10,
            sortBy: storedSort || DEFAULT_SORT,
        },
        filterOutputMappings: {
            upc: upcMapper,
            cxHClient: "cxhClient",
            startDate: "shipment.startDate",
        },
        onSearchParamsSet: (filters) => {
            setStorageValue(storedFilterName, filters);
        },
        callback: () => {
            setStorageValue("dup-stage-filter", defaultFilterValues);
            search(defaultFilterValues);
            setUpcs(undefined);
            resetColumns();
        },
    });

    useEffect(() => {
        search(storedFilters);

        eventEmitter.on("UPDATE_NOTE_SETTINGS", updateNoteSettings);

        return () => {
            eventEmitter.removeListener("UPDATE_NOTE_SETTINGS", updateNoteSettings);
        };
    }, []);

    useEffect(() => {
        if (!isWindowVisible) {
            return;
        }

        searchTasks({ searchParams, pagingParams, isUsingExternalFilters: false });
    }, [isWindowVisible, searchParams, pagingParams]);

    useEffect(() => {
        if (isSuccess) {
            setTotalCount(taskList.totalRecordCount);
            setTaskListItems(taskList.items);
        }
    }, [isSuccess, taskList]);

    useEffect(() => {
        // don't trigger search when "upcs" is initialized
        if (upcs !== undefined) {
            searchByUpcs(upcs, upcMapper);
        }
    }, [upcs]);

    useEffect(() => {
        setStorageValue("dup-stage-settings", { pageSize: pageSize });
    }, [pageSize]);

    useEffect(() => {
        setStorageValue("dup-stage-sort", storedSort);
    }, [storedSort]);

    const updateNoteSettings = (newSettings) => {
        setNoteSettings({ ...newSettings, taskType: taskType });
    };

    const onBINClick = (event, shipmentUrl) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.ctrlKey || event.metaKey) {
            window.open(`${shipmentUrl}`, "_blank");
        }
    };

    const handlePageChange = (page) => {
        setPage(page);
    };

    const closeNotesModal = () => {
        setNoteSettings(null);
    };

    const onRowClick = (s, rowInfo) => {
        const key = rowInfo && rowInfo.original.key;
        const style = {
            cursor: "pointer",
        };

        return {
            onDoubleClick: (e) => navigate(`${section.path}${key}`),
            style: style,
        };
    };

    const closeModal = () => {
        setShowModalColumns(false);
    };

    const openColumnChooser = () => {
        setShowModalColumns(true);
    };

    const updateUserColumns = (columns) => {
        const selectedColumns = getVisibleColumnsKeys(columns);
        setStorageValue("task-list-columns-duplicatestaging", selectedColumns);
        setVisibleColumns(columns);
        closeModal();
    };

    const resetColumns = () => {
        localStorage.removeItem("task-list-columns-duplicatestaging");
        closeModal();
        setVisibleColumns(getUserColumns(true));
    };

    const getVisibleColumnsKeys = (columns) =>
        Object.keys(visibleColumns)
            .reduce((arr, key) => {
                if (columns[key].value) {
                    arr.push(key);
                }

                return arr;
            }, [])
            .join(",");

    return (
        <div className="container">
            <h1>Duplicate Staging</h1>

            <TaskListExternalFilters
                upcs={upcs}
                setUpcs={setUpcs}
                searchByUpcs={searchByUpcs}
                resetFilters={resetFilters}
                showModalColumns={showModalColumns}
                onShowModalColumns={openColumnChooser}
                defaultFilterValues={defaultFilterValues}
            />
            <Modal
                show={showModalColumns}
                onClose={closeModal}
            >
                <ColumnChooser
                    type={taskType}
                    updateUserColumns={updateUserColumns}
                    resetColumns={resetColumns}
                    userColumns={visibleColumns}
                />
            </Modal>

            <ReactTable
                manual
                data={taskListItems}
                filtered={filteredValues}
                sortable
                columns={createColumns(
                    null,
                    null,
                    null,
                    visibleColumns,
                    onBINClick,
                    null,
                    locations,
                    TASK_STATUSES,
                    null,
                    null,
                    null,
                    null,
                    taskType,
                    true,
                    settings
                )}
                multiSort
                minRows={5}
                totalRecords={totalCount}
                showPagination
                PaginationComponent={ReactTablePagination}
                onPageChange={handlePageChange}
                onPageSizeChange={setPageSize}
                onSortedChange={setSortBy}
                onFilteredChange={search}
                sorted={sortBy}
                page={pageNumber}
                pageSize={pageSize}
                pages={totalPages}
                getTrProps={onRowClick}
            />
            <Modal
                show={!!noteSettings}
                onClose={closeNotesModal}
            >
                <NoteLoader settings={noteSettings} />
            </Modal>
        </div>
    );
};

export default DuplicateStagingIndex;
